import type {
  AmhsStreamDispatchPolicy,
  AmhsStreamOhtAssignment,
  AmhsStreamRunRequest,
  AmhsStreamVehicleSelectionKey,
} from '../../types/amhsStream';

export const AMHS_STREAM_DISPATCH_POLICIES: AmhsStreamDispatchPolicy[] = [
  'nearest',
  'fifo',
  'ortools',
];

export const AMHS_STREAM_DISPATCH_LABELS: Record<AmhsStreamDispatchPolicy, string> = {
  nearest: 'Nearest',
  fifo: 'FIFO',
  ortools: 'OR-Tools',
};

export const AMHS_STREAM_DISPATCH_DESCRIPTIONS: Record<AmhsStreamDispatchPolicy, string> = {
  nearest:
    'Activate the closest ready hop first and assign the nearest free OHT (default live behavior).',
  fifo: 'Activate hops in arrival order and prefer idle vehicles over empty-travel distance.',
  ortools:
    'Assign each tick with rolling-wave CP-SAT optimization; falls back to nearest-style greedy if the solver fails.',
};

const VEHICLE_ORDER_BY_POLICY: Record<
  AmhsStreamDispatchPolicy,
  AmhsStreamVehicleSelectionKey[]
> = {
  nearest: ['deadhead', 'freeAt', 'jobsSoFar', 'ohtId'],
  fifo: ['freeAt', 'jobsSoFar', 'ohtId'],
  ortools: ['deadhead', 'freeAt', 'jobsSoFar', 'ohtId'],
};

export const DEFAULT_STREAM_ORTOOLS_TIME_LIMIT_S = 0.05;

export function normalizeStreamDispatchPolicy(
  value: unknown
): AmhsStreamDispatchPolicy | undefined {
  if (typeof value !== 'string') return undefined;
  const token = value.trim().toLowerCase();
  if (token === 'greedy' || token === 'deadhead') return 'nearest';
  if (AMHS_STREAM_DISPATCH_POLICIES.includes(token as AmhsStreamDispatchPolicy)) {
    return token as AmhsStreamDispatchPolicy;
  }
  return undefined;
}

export function resolveStreamDispatchPolicy(
  request?: Partial<AmhsStreamOhtAssignment> | null,
  projectMetadata?: Record<string, unknown> | null
): AmhsStreamDispatchPolicy {
  const fromRequest = normalizeStreamDispatchPolicy(request?.dispatchPolicy);
  if (fromRequest) return fromRequest;

  const metadata = projectMetadata?.dispatchPolicy ?? projectMetadata?.ohtAssignment;
  if (metadata && typeof metadata === 'object') {
    const nested = normalizeStreamDispatchPolicy(
      (metadata as Record<string, unknown>).dispatchPolicy
    );
    if (nested) return nested;
  }
  return normalizeStreamDispatchPolicy(projectMetadata?.dispatchPolicy) ?? 'nearest';
}

export function buildStreamOhtAssignment(
  policy: AmhsStreamDispatchPolicy,
  overrides?: Partial<AmhsStreamOhtAssignment>
): AmhsStreamOhtAssignment {
  const vehicleOrder =
    overrides?.vehicleSelection?.order ?? [...VEHICLE_ORDER_BY_POLICY[policy]];

  const shared: AmhsStreamOhtAssignment = {
    enabled: overrides?.enabled ?? true,
    dispatchPolicy: policy,
    preferPreferredOht: overrides?.preferPreferredOht ?? false,
    allowIntrabayEmptyTravel: overrides?.allowIntrabayEmptyTravel ?? true,
    vehicleSelection: { order: vehicleOrder },
  };

  if (policy !== 'ortools') {
    return shared;
  }

  return {
    ...shared,
    useOrtools: overrides?.useOrtools ?? true,
    forceOrtools: overrides?.forceOrtools,
    ortoolsTimeLimitS:
      overrides?.ortoolsTimeLimitS ?? DEFAULT_STREAM_ORTOOLS_TIME_LIMIT_S,
    intelligentSharedOht: overrides?.intelligentSharedOht ?? false,
    sharedOhtMinGainSec: overrides?.sharedOhtMinGainSec ?? 30,
    intelligentDedicatedAssist: overrides?.intelligentDedicatedAssist ?? false,
    dedicatedJobsFirst: overrides?.dedicatedJobsFirst ?? true,
  };
}

export function resolveSavedOhtAssignment(
  projectMetadata?: Record<string, unknown> | null
): Partial<AmhsStreamOhtAssignment> | undefined {
  const raw =
    projectMetadata?.ohtAssignment && typeof projectMetadata.ohtAssignment === 'object'
      ? (projectMetadata.ohtAssignment as Record<string, unknown>)
      : null;
  if (!raw && projectMetadata?.dispatchPolicy == null) return undefined;

  const resolved: Partial<AmhsStreamOhtAssignment> = {};
  const policy = normalizeStreamDispatchPolicy(
    raw?.dispatchPolicy ?? projectMetadata?.dispatchPolicy
  );
  if (policy) resolved.dispatchPolicy = policy;

  if (raw) {
    if (typeof raw.preferPreferredOht === 'boolean') {
      resolved.preferPreferredOht = raw.preferPreferredOht;
    }
    if (typeof raw.allowIntrabayEmptyTravel === 'boolean') {
      resolved.allowIntrabayEmptyTravel = raw.allowIntrabayEmptyTravel;
    }
    if (typeof raw.useOrtools === 'boolean') resolved.useOrtools = raw.useOrtools;
    if (typeof raw.forceOrtools === 'boolean') resolved.forceOrtools = raw.forceOrtools;
    if (
      typeof raw.ortoolsTimeLimitS === 'number' &&
      Number.isFinite(raw.ortoolsTimeLimitS) &&
      raw.ortoolsTimeLimitS > 0
    ) {
      resolved.ortoolsTimeLimitS = raw.ortoolsTimeLimitS;
    }
    if (typeof raw.intelligentSharedOht === 'boolean') {
      resolved.intelligentSharedOht = raw.intelligentSharedOht;
    }
    if (
      typeof raw.sharedOhtMinGainSec === 'number' &&
      Number.isFinite(raw.sharedOhtMinGainSec) &&
      raw.sharedOhtMinGainSec >= 0
    ) {
      resolved.sharedOhtMinGainSec = raw.sharedOhtMinGainSec;
    }
    if (typeof raw.intelligentDedicatedAssist === 'boolean') {
      resolved.intelligentDedicatedAssist = raw.intelligentDedicatedAssist;
    }
    if (typeof raw.dedicatedJobsFirst === 'boolean') {
      resolved.dedicatedJobsFirst = raw.dedicatedJobsFirst;
    }
  }

  return Object.keys(resolved).length > 0 ? resolved : undefined;
}

export function applyStreamDispatchPolicy(
  request: AmhsStreamRunRequest,
  policy: AmhsStreamDispatchPolicy
): AmhsStreamRunRequest {
  const current = request.ohtAssignment;
  const next = buildStreamOhtAssignment(policy, {
    preferPreferredOht: current.preferPreferredOht,
    allowIntrabayEmptyTravel: current.allowIntrabayEmptyTravel,
    ...(policy === 'ortools'
      ? {
          useOrtools: current.useOrtools ?? true,
          ortoolsTimeLimitS:
            current.ortoolsTimeLimitS ?? DEFAULT_STREAM_ORTOOLS_TIME_LIMIT_S,
          intelligentSharedOht: current.intelligentSharedOht ?? false,
          sharedOhtMinGainSec: current.sharedOhtMinGainSec ?? 30,
          intelligentDedicatedAssist: current.intelligentDedicatedAssist ?? false,
          dedicatedJobsFirst: current.dedicatedJobsFirst ?? true,
        }
      : {}),
  });
  return {
    ...request,
    ohtAssignment: next,
  };
}

export function dispatchPolicyFromRequest(
  request: AmhsStreamRunRequest
): AmhsStreamDispatchPolicy {
  return resolveStreamDispatchPolicy(request.ohtAssignment);
}
